'use client'

import Box from '@mui/material/Box'
import Image from 'next/image'
import { ReactNode } from 'react'

import TitleAndParagraph from '@/components/TitleAndParagraph'
import TwoColumnLayout, { ColumnWrapOrder } from '@/components/TwoColumnLayout'

const SquareMedia = ({
    src,
    alt,
    smallerImage,
}: {
    src: string
    alt: string
    smallerImage?: boolean
}) => {
    const isVideo = src.endsWith('.mp4') || src.endsWith('.webm') || src.endsWith('.mov')
    const isGif = src.endsWith('.gif')

    return (
        <Box
            sx={{
                display: 'flex',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    height: '0',
                    paddingBottom: smallerImage ? '70%' : '100%',
                    position: 'relative',
                    img: {
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        ...(isGif && {
                            width: '100%',
                            height: 'auto',
                            maxWidth: '100%',
                            objectFit: 'contain',
                            transform: 'translateY(-50%)',
                            top: '50%',
                        }),
                    },
                    video: {
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                    },
                }}
            >
                {isVideo ? (
                    <video src={src} autoPlay loop muted playsInline aria-label={alt} />
                ) : (
                    <Image src={src} alt={alt} width={440} height={440} />
                )}
            </Box>
        </Box>
    )
}

const TwoColumnsWithSquareImage = ({
    title,
    paragraph,
    image,
    imageOnRight,
    snippet,
    smallerImage,
}: {
    title: string
    paragraph: ReactNode
    image: { src: string; alt: string }
    imageOnRight?: boolean
    snippet?: ReactNode
    smallerImage?: boolean
}) => {
    const squareMedia = <SquareMedia src={image.src} alt={image.alt} smallerImage={smallerImage} />
    const titleAndParagraph = (
        <TitleAndParagraph title={title} paragraph={paragraph} snippet={snippet} />
    )

    return (
        <Box
            sx={{
                position: 'relative',
            }}
        >
            {imageOnRight ? (
                <TwoColumnLayout
                    columnWrapOrderOnMobile={ColumnWrapOrder.NORMAL}
                    leftCol={6}
                    rightCol={5}
                >
                    {titleAndParagraph}
                    {squareMedia}
                </TwoColumnLayout>
            ) : (
                <TwoColumnLayout
                    columnWrapOrderOnMobile={ColumnWrapOrder.REVERSE}
                    leftCol={5}
                    rightCol={6}
                >
                    {squareMedia}
                    {titleAndParagraph}
                </TwoColumnLayout>
            )}
        </Box>
    )
}

export default TwoColumnsWithSquareImage
